import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { DailyReportEntry } from '../../types';
import {
  Edit2,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Printer,
  Download,
  Copy,
  Check,
} from 'lucide-react';
import { StorageService } from '../../lib/storage';
import { DailyReportModal } from './DailyReportModal';
import { getLocalDateString } from '../../lib/dateUtils';

interface ColumnDef {
  key: keyof DailyReportEntry;
  type: 'number' | 'checkbox' | 'decimal';
  label: string;
  group: string;
  width?: string;
  textColor?: string;
}

const COLUMNS_META: ColumnDef[] = [
  // Quran
  { key: 'quranTilawat', type: 'number', label: 'Tilawat', group: 'quran', textColor: 'text-emerald-700 dark:text-emerald-300' },
  { key: 'quranTafseer', type: 'number', label: 'Tafseer', group: 'quran', textColor: 'text-emerald-700 dark:text-emerald-300' },
  // Hadith
  { key: 'hadithCount', type: 'number', label: 'Hadith', group: 'hadith', textColor: 'text-purple-700 dark:text-purple-300' },
  // Salah
  { key: 'salahJamat', type: 'number', label: 'Jamat', group: 'salah', textColor: 'text-sky-700 dark:text-sky-300' },
  { key: 'salahQaza', type: 'number', label: 'Qaza', group: 'salah', textColor: 'text-rose-600 dark:text-rose-400' },
  // Literature
  { key: 'literatureGeneral', type: 'number', label: 'General', group: 'literature', textColor: 'text-amber-700 dark:text-amber-300' },
  { key: 'literatureSyllabus', type: 'number', label: 'Syllabus', group: 'literature', textColor: 'text-amber-700 dark:text-amber-300' },
  // Academic
  { key: 'academicStudyHours', type: 'decimal', label: 'Academic', group: 'academic', textColor: 'text-yellow-700 dark:text-yellow-300' },
  // Classes
  { key: 'classCount', type: 'number', label: 'Held', group: 'classes', textColor: 'text-teal-700 dark:text-teal-300' },
  { key: 'classAttended', type: 'number', label: 'Attnd', group: 'classes', textColor: 'text-teal-700 dark:text-teal-300' },
  // Contacts
  { key: 'contactMember', type: 'number', label: 'Member', group: 'contacts' },
  { key: 'contactAssociate', type: 'number', label: 'Assoc', group: 'contacts' },
  { key: 'contactWorker', type: 'number', label: 'Worker', group: 'contacts' },
  { key: 'contactSupporter', type: 'number', label: 'Supprt', group: 'contacts' },
  { key: 'contactFriend', type: 'number', label: 'Friend', group: 'contacts' },
  // Special Outreach
  { key: 'contactTalented', type: 'number', label: 'Talent', group: 'outreach' },
  { key: 'contactWellWisher', type: 'number', label: 'Wisher', group: 'outreach' },
  { key: 'contactMukallif', type: 'number', label: 'Contact', group: 'outreach' },
  // Distribution
  { key: 'distLiterature', type: 'number', label: 'Books', group: 'dist' },
  { key: 'distMagazine', type: 'number', label: 'Mag', group: 'dist' },
  { key: 'distStickerCard', type: 'number', label: 'Sticker', group: 'dist' },
  { key: 'distGift', type: 'number', label: 'Gift', group: 'dist' },
  // Duties
  { key: 'dutyDawahHours', type: 'decimal', label: 'Dawah', group: 'duties', textColor: 'text-rose-600 dark:text-rose-300' },
  { key: 'dutyOtherHours', type: 'decimal', label: 'Other', group: 'duties', textColor: 'text-rose-600 dark:text-rose-300' },
  // Social
  { key: 'socialMediaHours', type: 'decimal', label: 'Social', group: 'social' },
  // Checkboxes
  { key: 'newspaperRead', type: 'checkbox', label: 'Paper', group: 'checks' },
  { key: 'exerciseDone', type: 'checkbox', label: 'Exercise', group: 'checks' },
  { key: 'selfAccountability', type: 'checkbox', label: 'Muhasabah', group: 'checks' },
  // Sleep
  { key: 'sleepHours', type: 'decimal', label: 'Sleep', group: 'sleep', textColor: 'text-blue-700 dark:text-blue-300 font-bold' },
];

const TOTAL_COLUMNS = COLUMNS_META.length; // 29

export const PersonalReportTable: React.FC = () => {
  const {
    user,
    personalReports,
    updateDailyReport,
    bulkUpdateDailyReports,
    language,
  } = useApp();

  const today = new Date();
  const [currentYear, setCurrentYear] = useState<number>(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(today.getMonth());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDateForModal, setSelectedDateForModal] = useState<string>(
    getLocalDateString(today)
  );


  // Spreadsheet Selection State
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [hasMovedDuringDrag, setHasMovedDuringDrag] = useState<boolean>(false);
  const [selectionStart, setSelectionStart] = useState<{ row: number; col: number } | null>({ row: 0, col: 0 });
  const [selectionEnd, setSelectionEnd] = useState<{ row: number; col: number } | null>({ row: 0, col: 0 });

  // Auto-fill Drag State (Google Sheets corner square)
  const [isFillDragging, setIsFillDragging] = useState<boolean>(false);
  const [fillEnd, setFillEnd] = useState<{ row: number; col: number } | null>(null);

  // Inline Cell Edit State
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const isCommittingRef = useRef<boolean>(false);
  const lastClickRef = useRef<{ row: number; col: number; time: number } | null>(null);

  // Toast / Copy Feedback State
  const [copyFeedback, setCopyFeedback] = useState<boolean>(false);

  // Container ref
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Month navigation
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const monthNamesEn = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthLabel = `${monthNamesEn[currentMonth]} ${currentYear}`;
  const monthPrefix = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Selection bounding box calculation
  const selectedRange = useMemo(() => {
    if (!selectionStart || !selectionEnd) return null;
    return {
      minRow: Math.min(selectionStart.row, selectionEnd.row),
      maxRow: Math.max(selectionStart.row, selectionEnd.row),
      minCol: Math.min(selectionStart.col, selectionEnd.col),
      maxCol: Math.max(selectionStart.col, selectionEnd.col),
    };
  }, [selectionStart, selectionEnd]);

  // Fill Drag Bounding Box
  const fillRange = useMemo(() => {
    if (!isFillDragging || !selectedRange || !fillEnd) return null;
    return {
      minRow: Math.min(selectedRange.minRow, fillEnd.row),
      maxRow: Math.max(selectedRange.maxRow, fillEnd.row),
      minCol: Math.min(selectedRange.minCol, fillEnd.col),
      maxCol: Math.max(selectedRange.maxCol, fillEnd.col),
    };
  }, [isFillDragging, selectedRange, fillEnd]);

  const selectedCellCount = useMemo(() => {
    if (!selectedRange) return 0;
    return (selectedRange.maxRow - selectedRange.minRow + 1) * (selectedRange.maxCol - selectedRange.minCol + 1);
  }, [selectedRange]);

  // Build monthly rows (01 to 31)
  const monthlyRows = useMemo(() => {
    const rows = [];
    const weekdaysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(currentYear, currentMonth, day);
      const dayOfWeek = dateObj.getDay();
      const dateStr = `${monthPrefix}-${String(day).padStart(2, '0')}`;
      const isFriday = dayOfWeek === 5;
      const isToday =
        today.getFullYear() === currentYear &&
        today.getMonth() === currentMonth &&
        today.getDate() === day;

      const existingData = personalReports.find((r) => r.date === dateStr) || { date: dateStr };

      rows.push({
        dayNum: day,
        dayName: weekdaysEn[dayOfWeek],
        dateStr,
        isFriday,
        isToday,
        data: existingData,
      });
    }

    return rows;
  }, [currentYear, currentMonth, daysInMonth, monthPrefix, personalReports]);

  // Cell Selection Position Helpers
  const isCellSelected = useCallback(
    (r: number, c: number) => {
      if (!selectedRange) return false;
      return (
        r >= selectedRange.minRow &&
        r <= selectedRange.maxRow &&
        c >= selectedRange.minCol &&
        c <= selectedRange.maxCol
      );
    },
    [selectedRange]
  );

  const isCellInFill = useCallback(
    (r: number, c: number) => {
      if (!fillRange) return false;
      return (
        r >= fillRange.minRow &&
        r <= fillRange.maxRow &&
        c >= fillRange.minCol &&
        c <= fillRange.maxCol
      );
    },
    [fillRange]
  );

  const isAnchorCell = useCallback(
    (r: number, c: number) => {
      return selectionStart?.row === r && selectionStart?.col === c;
    },
    [selectionStart]
  );

  // Auto-scroll selected cell into view
  const scrollToCell = useCallback((r: number, c: number) => {
    const cellEl = document.getElementById(`cell-${r}-${c}`);
    if (cellEl) {
      cellEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, []);

  // Selection Aggregates (Sum, Average, Count, Min, Max) for Live Google Sheets Status Bar
  const selectionStats = useMemo(() => {
    if (!selectedRange || selectedCellCount <= 1) return null;
    let numSum = 0;
    let numCount = 0;
    let checkCount = 0;
    let minVal = Infinity;
    let maxVal = -Infinity;

    for (let r = selectedRange.minRow; r <= selectedRange.maxRow; r++) {
      const rowData = monthlyRows[r]?.data as any;
      if (!rowData) continue;
      for (let c = selectedRange.minCol; c <= selectedRange.maxCol; c++) {
        const colMeta = COLUMNS_META[c];
        if (!colMeta) continue;
        const val = rowData[colMeta.key];

        if (colMeta.type === 'checkbox') {
          if (val) checkCount++;
        } else if (val !== undefined && val !== null && val !== '') {
          const num = Number(val);
          if (!isNaN(num)) {
            numSum += num;
            numCount++;
            if (num < minVal) minVal = num;
            if (num > maxVal) maxVal = num;
          }
        }
      }
    }

    const avg = numCount > 0 ? (numSum / numCount).toFixed(1) : '0';

    return {
      cellCount: selectedCellCount,
      numCount,
      numSum: Number(numSum.toFixed(2)),
      avg,
      min: numCount > 0 ? minVal : 0,
      max: numCount > 0 ? maxVal : 0,
      checkCount,
    };
  }, [selectedRange, selectedCellCount, monthlyRows]);

  // Google Sheets Active Cell Info (Row Date, Field, Current Value)
  const activeCellInfo = useMemo(() => {
    if (!selectionStart) return null;
    const rowObj = monthlyRows[selectionStart.row];
    const colDef = COLUMNS_META[selectionStart.col];
    if (!rowObj || !colDef) return null;
    const rawVal = (rowObj.data as any)[colDef.key];
    const displayVal =
      colDef.type === 'checkbox'
        ? rawVal
          ? '✓ Checked'
          : 'Unchecked'
        : rawVal !== undefined && rawVal !== null && rawVal !== ''
        ? String(rawVal)
        : '0';
    return {
      date: `${String(rowObj.dayNum).padStart(2, '0')} ${monthNamesEn[currentMonth]} (${rowObj.dayName})`,
      field: colDef.label,
      value: displayVal,
    };
  }, [selectionStart, monthlyRows, currentMonth]);

  // Aggregate Column Totals (Sheet Footer)
  const totals = useMemo(() => {
    const sumMap: Record<string, number> = {};
    let totalSleep = 0;
    let sleepCount = 0;

    COLUMNS_META.forEach((col) => {
      sumMap[col.key] = 0;
    });

    monthlyRows.forEach((r) => {
      const d = r.data as any;
      COLUMNS_META.forEach((col) => {
        if (col.type === 'checkbox') {
          if (d[col.key]) sumMap[col.key] += 1;
        } else if (d[col.key] !== undefined && d[col.key] !== null) {
          sumMap[col.key] += Number(d[col.key]) || 0;
        }
      });

      if (d.sleepHours && d.sleepHours > 0) {
        totalSleep += d.sleepHours;
        sleepCount += 1;
      }
    });

    const avgSleep = sleepCount > 0 ? (totalSleep / sleepCount).toFixed(1) : '0.0';

    return {
      sumMap,
      avgSleep,
    };
  }, [monthlyRows]);

  // Update Single Cell
  const handleCellChange = useCallback(
    (dateStr: string, field: keyof DailyReportEntry, val: any) => {
      const existing = personalReports.find((r) => r.date === dateStr) || { date: dateStr };
      const updated: DailyReportEntry = {
        ...existing,
        [field]: val,
      };
      updateDailyReport(updated);
    },
    [personalReports, updateDailyReport]
  );

  // Commit Active Cell Edit (with optional navigation offset like Google Sheets)
  const commitEdit = useCallback(
    (moveTo?: { rowOffset: number; colOffset: number }) => {
      if (!editingCell) return;
      if (isCommittingRef.current) return;
      isCommittingRef.current = true;

      const { row: r, col: c } = editingCell;
      const colDef = COLUMNS_META[c];
      const rowObj = monthlyRows[r];
      if (!colDef || !rowObj) {
        setEditingCell(null);
        isCommittingRef.current = false;
        return;
      }

      let parsedVal: any = undefined;
      if (colDef.type === 'checkbox') {
        parsedVal = editValue === 'true';
      } else if (editValue.trim() !== '') {
        parsedVal = colDef.type === 'decimal' ? parseFloat(editValue) || 0 : parseInt(editValue, 10) || 0;
      }

      handleCellChange(rowObj.dateStr, colDef.key, parsedVal);
      setEditingCell(null);

      if (moveTo) {
        const nextR = Math.max(0, Math.min(daysInMonth - 1, r + moveTo.rowOffset));
        const nextC = Math.max(0, Math.min(TOTAL_COLUMNS - 1, c + moveTo.colOffset));
        setSelectionStart({ row: nextR, col: nextC });
        setSelectionEnd({ row: nextR, col: nextC });
        scrollToCell(nextR, nextC);
      }

      setTimeout(() => {
        isCommittingRef.current = false;
      }, 50);
    },
    [editingCell, editValue, monthlyRows, handleCellChange, daysInMonth, scrollToCell]
  );

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  // Start Editing Cell (Google Sheets In-Place Edit Mode)
  const startEditing = useCallback(
    (r: number, c: number, initialChar?: string) => {
      const colDef = COLUMNS_META[c];
      const rowObj = monthlyRows[r];
      if (!colDef || !rowObj) return;

      if (colDef.type === 'checkbox') {
        // Direct toggle for checkboxes
        const currentVal = (rowObj.data as any)[colDef.key];
        handleCellChange(rowObj.dateStr, colDef.key, !currentVal);
        return;
      }

      const currentVal = (rowObj.data as any)[colDef.key];
      const startStr =
        initialChar !== undefined
          ? initialChar
          : currentVal !== undefined && currentVal !== null
          ? String(currentVal)
          : '';
      setEditValue(startStr);
      setEditingCell({ row: r, col: c });
      setSelectionStart({ row: r, col: c });
      setSelectionEnd({ row: r, col: c });
    },
    [monthlyRows, handleCellChange]
  );

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  // Delete all values in selected range
  const deleteSelectedRange = useCallback(() => {
    if (!selectedRange) return;
    const { minRow, maxRow, minCol, maxCol } = selectedRange;
    const updates: DailyReportEntry[] = [];

    for (let r = minRow; r <= maxRow; r++) {
      if (r >= 0 && r < monthlyRows.length) {
        const row = monthlyRows[r];
        const existing = personalReports.find((p) => p.date === row.dateStr) || { date: row.dateStr };
        const updatedRow: DailyReportEntry = { ...existing };

        for (let c = minCol; c <= maxCol; c++) {
          const colDef = COLUMNS_META[c];
          if (colDef) {
            if (colDef.type === 'checkbox') {
              (updatedRow as any)[colDef.key] = false;
            } else {
              (updatedRow as any)[colDef.key] = undefined;
            }
          }
        }
        updates.push(updatedRow);
      }
    }

    if (updates.length > 0) {
      bulkUpdateDailyReports(updates);
    }
  }, [selectedRange, monthlyRows, personalReports, bulkUpdateDailyReports]);

  // Copy Selected Range to Clipboard (TSV format compatible with Excel & Google Sheets)
  const copySelectedRange = useCallback(() => {
    if (!selectedRange) return;
    const { minRow, maxRow, minCol, maxCol } = selectedRange;
    const rows: string[] = [];

    for (let r = minRow; r <= maxRow; r++) {
      const rowData = monthlyRows[r]?.data as any;
      const rowCols: string[] = [];
      for (let c = minCol; c <= maxCol; c++) {
        const colDef = COLUMNS_META[c];
        if (!colDef || !rowData) {
          rowCols.push('');
          continue;
        }
        const val = rowData[colDef.key];
        if (colDef.type === 'checkbox') {
          rowCols.push(val ? 'TRUE' : 'FALSE');
        } else {
          rowCols.push(val !== undefined && val !== null ? String(val) : '');
        }
      }
      rows.push(rowCols.join('\t'));
    }

    const tsvData = rows.join('\n');
    navigator.clipboard.writeText(tsvData).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 1500);
    });
  }, [selectedRange, monthlyRows]);

  // Paste from Clipboard into Table (Supports Google Sheets / Excel 2D tables)
  const pasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;

      const rawRows = text.split(/\r?\n/).filter((r) => r.length > 0);
      if (rawRows.length === 0) return;

      const pasteMatrix = rawRows.map((r) => r.split('\t'));
      const startR = selectedRange ? selectedRange.minRow : 0;
      const startC = selectedRange ? selectedRange.minCol : 0;

      // If pasting 1 value into a larger selected range (Excel/Sheets fill)
      const isSingleVal = pasteMatrix.length === 1 && pasteMatrix[0].length === 1;
      const updatesMap = new Map<string, DailyReportEntry>();

      if (isSingleVal && selectedRange && selectedCellCount > 1) {
        const singleValStr = pasteMatrix[0][0].trim();
        for (let r = selectedRange.minRow; r <= selectedRange.maxRow; r++) {
          const rowObj = monthlyRows[r];
          if (!rowObj) continue;
          const existing = updatesMap.get(rowObj.dateStr) || personalReports.find((p) => p.date === rowObj.dateStr) || { date: rowObj.dateStr };
          const updated = { ...existing };

          for (let c = selectedRange.minCol; c <= selectedRange.maxCol; c++) {
            const colDef = COLUMNS_META[c];
            if (!colDef) continue;
            if (colDef.type === 'checkbox') {
              (updated as any)[colDef.key] = ['true', '1', 'yes', 'TRUE'].includes(singleValStr.toLowerCase());
            } else if (singleValStr === '') {
              (updated as any)[colDef.key] = undefined;
            } else {
              (updated as any)[colDef.key] = colDef.type === 'decimal' ? parseFloat(singleValStr) || 0 : parseInt(singleValStr, 10) || 0;
            }
          }
          updatesMap.set(rowObj.dateStr, updated);
        }
      } else {
        // Multi-cell matrix paste
        for (let rOffset = 0; rOffset < pasteMatrix.length; rOffset++) {
          const pRow = pasteMatrix[rOffset];
          const targetR = startR + rOffset;
          if (targetR >= monthlyRows.length) break;
          const rowObj = monthlyRows[targetR];
          if (!rowObj) continue;

          const existing = updatesMap.get(rowObj.dateStr) || personalReports.find((p) => p.date === rowObj.dateStr) || { date: rowObj.dateStr };
          const updated = { ...existing };

          for (let cOffset = 0; cOffset < pRow.length; cOffset++) {
            const cellVal = pRow[cOffset];
            const targetC = startC + cOffset;
            if (targetC >= TOTAL_COLUMNS) break;
            const colDef = COLUMNS_META[targetC];
            if (!colDef) continue;

            const trimmed = cellVal.trim();
            if (colDef.type === 'checkbox') {
              (updated as any)[colDef.key] = ['true', '1', 'yes', 'TRUE', 'x', '✓'].includes(trimmed.toLowerCase());
            } else if (trimmed === '') {
              (updated as any)[colDef.key] = undefined;
            } else {
              (updated as any)[colDef.key] = colDef.type === 'decimal' ? parseFloat(trimmed) || 0 : parseInt(trimmed, 10) || 0;
            }
          }

          updatesMap.set(rowObj.dateStr, updated);
        }
      }

      const updatesList = Array.from(updatesMap.values());
      if (updatesList.length > 0) {
        bulkUpdateDailyReports(updatesList);
      }
    } catch (err) {
      console.warn('Clipboard paste failed:', err);
    }
  }, [selectedRange, selectedCellCount, monthlyRows, personalReports, bulkUpdateDailyReports]);

  // Execute Auto-fill drag logic (Google Sheets fill-down / fill-right)
  const applyFillDrag = useCallback(() => {
    if (!selectedRange || !fillRange) return;
    const sourceRows = selectedRange.maxRow - selectedRange.minRow + 1;
    const sourceCols = selectedRange.maxCol - selectedRange.minCol + 1;
    const updatesMap = new Map<string, DailyReportEntry>();

    for (let r = fillRange.minRow; r <= fillRange.maxRow; r++) {
      const targetRowObj = monthlyRows[r];
      if (!targetRowObj) continue;

      const existing = updatesMap.get(targetRowObj.dateStr) || personalReports.find((p) => p.date === targetRowObj.dateStr) || { date: targetRowObj.dateStr };
      const updated = { ...existing };

      for (let c = fillRange.minCol; c <= fillRange.maxCol; c++) {
        // Map to corresponding source cell inside selectedRange
        const srcR = selectedRange.minRow + ((r - fillRange.minRow) % sourceRows);
        const srcC = selectedRange.minCol + ((c - fillRange.minCol) % sourceCols);
        const srcRowObj = monthlyRows[srcR];
        const colDef = COLUMNS_META[c];
        const srcColDef = COLUMNS_META[srcC];

        if (srcRowObj && colDef && srcColDef) {
          const srcVal = (srcRowObj.data as any)[srcColDef.key];
          (updated as any)[colDef.key] = srcVal;
        }
      }
      updatesMap.set(targetRowObj.dateStr, updated);
    }

    const updatesList = Array.from(updatesMap.values());
    if (updatesList.length > 0) {
      bulkUpdateDailyReports(updatesList);
    }

    setSelectionStart({ row: fillRange.minRow, col: fillRange.minCol });
    setSelectionEnd({ row: fillRange.maxRow, col: fillRange.maxCol });
  }, [selectedRange, fillRange, monthlyRows, personalReports, bulkUpdateDailyReports]);

  // Mouse Handlers for Fluid Range Selection
  const handleCellMouseDown = (e: React.MouseEvent, r: number, c: number) => {
    if (e.button !== 0) return; // Only left click

    // If shift key held down, extend selection
    if (e.shiftKey && selectionStart) {
      e.preventDefault();
      if (editingCell) commitEdit();
      setSelectionEnd({ row: r, col: c });
      return;
    }

    // If already editing this exact cell, let input handle cursor
    if (editingCell && editingCell.row === r && editingCell.col === c) {
      return;
    }

    // Commit previous edit if clicking a different cell
    if (editingCell) {
      commitEdit();
    }

    setIsMouseDown(true);
    setHasMovedDuringDrag(false);
    setSelectionStart({ row: r, col: c });
    setSelectionEnd({ row: r, col: c });
  };

  const handleCellMouseEnter = (r: number, c: number) => {
    if (isMouseDown) {
      // Only mark as drag if the cursor has actually moved to a DIFFERENT cell
      if (selectionStart && (selectionStart.row !== r || selectionStart.col !== c)) {
        setHasMovedDuringDrag(true);
        // When dragging across cells, disable inline input to show selection box
        if (editingCell) {
          setEditingCell(null);
        }
        setSelectionEnd({ row: r, col: c });
      }
    } else if (isFillDragging) {
      setFillEnd({ row: r, col: c });
    }
  };

  // Cell Click: 1st click selects cell, 2nd click on same cell or double-click enters edit mode
  const handleCellClick = (r: number, c: number) => {
    if (hasMovedDuringDrag) return;

    const colDef = COLUMNS_META[c];
    if (colDef && colDef.type === 'checkbox') {
      const rowObj = monthlyRows[r];
      if (rowObj) {
        handleCellChange(rowObj.dateStr, colDef.key, !(rowObj.data as any)[colDef.key]);
      }
      setSelectionStart({ row: r, col: c });
      setSelectionEnd({ row: r, col: c });
      return;
    }

    const now = Date.now();
    const isRapidClick =
      lastClickRef.current &&
      lastClickRef.current.row === r &&
      lastClickRef.current.col === c &&
      now - lastClickRef.current.time < 450;

    const isAlreadySelected =
      selectionStart &&
      selectionStart.row === r &&
      selectionStart.col === c &&
      selectedCellCount <= 1;

    lastClickRef.current = { row: r, col: c, time: now };

    const isTouch =
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 768);

    // On touch/mobile devices, open edit mode and numeric keyboard directly on tap!
    // On desktop, open if rapid double-click or clicking already-selected cell.
    if (isTouch || isRapidClick || isAlreadySelected) {
      startEditing(r, c);
      return;
    }

    // Otherwise, select the cell
    setSelectionStart({ row: r, col: c });
    setSelectionEnd({ row: r, col: c });
  };

  // Cell Double Click: Enter Edit Mode (Google Sheets double-click behavior)
  const handleCellDoubleClick = (r: number, c: number) => {
    startEditing(r, c);
  };

  // Header Click Handlers
  const handleRowHeaderClick = (e: React.MouseEvent, r: number) => {
    e.preventDefault();
    if (editingCell) commitEdit();
    if (e.shiftKey && selectionStart) {
      setSelectionEnd({ row: r, col: TOTAL_COLUMNS - 1 });
    } else {
      setSelectionStart({ row: r, col: 0 });
      setSelectionEnd({ row: r, col: TOTAL_COLUMNS - 1 });
    }
  };

  const handleColHeaderClick = (e: React.MouseEvent, c: number) => {
    e.preventDefault();
    if (editingCell) commitEdit();
    if (e.shiftKey && selectionStart) {
      setSelectionEnd({ row: daysInMonth - 1, col: c });
    } else {
      setSelectionStart({ row: 0, col: c });
      setSelectionEnd({ row: daysInMonth - 1, col: c });
    }
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editingCell) commitEdit();
    setSelectionStart({ row: 0, col: 0 });
    setSelectionEnd({ row: daysInMonth - 1, col: TOTAL_COLUMNS - 1 });
  };

  // Global Window MouseUp & PointerUp Listener for finishing drags (Android Phone Mouse & Desktop)
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isFillDragging) {
        applyFillDrag();
        setIsFillDragging(false);
        setFillEnd(null);
      }
      setIsMouseDown(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('pointerup', handleGlobalMouseUp);
    window.addEventListener('pointercancel', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('pointerup', handleGlobalMouseUp);
      window.removeEventListener('pointercancel', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isFillDragging, applyFillDrag]);

  // Keyboard Shortcuts (Up, Down, Left, Right, Enter, Tab)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isModalOpen) return;

      const activeEl = document.activeElement;
      const activeTag = activeEl?.tagName.toLowerCase();

      if (activeTag === 'input' || activeTag === 'textarea') {
        if (!editingCell) return;
      }

      const isInsideTable =
        tableContainerRef.current &&
        (tableContainerRef.current.contains(activeEl) ||
          tableContainerRef.current === activeEl ||
          tableContainerRef.current.contains(e.target as Node) ||
          Boolean(editingCell));

      if (!isInsideTable) return;

      // Clipboard Shortcuts
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (!editingCell && selectedRange) {
          e.preventDefault();
          copySelectedRange();
          return;
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (!editingCell && selectedRange) {
          e.preventDefault();
          pasteFromClipboard();
          return;
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        if (!editingCell) {
          e.preventDefault();
          setSelectionStart({ row: 0, col: 0 });
          setSelectionEnd({ row: daysInMonth - 1, col: TOTAL_COLUMNS - 1 });
          return;
        }
      }

      // If actively editing a cell: Enter, Tab, Escape
      if (editingCell) {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitEdit({ rowOffset: e.shiftKey ? -1 : 1, colOffset: 0 });
        } else if (e.key === 'Tab') {
          e.preventDefault();
          commitEdit({ rowOffset: 0, colOffset: e.shiftKey ? -1 : 1 });
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancelEdit();
        }
        return;
      }

      // Grid Navigation Mode (When not editing - pure Google Sheets behavior)
      const currentStart = selectionStart || { row: 0, col: 0 };
      const currentEnd = selectionEnd || { row: 0, col: 0 };

      if (e.key === 'Escape') {
        setSelectionStart(null);
        setSelectionEnd(null);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedRange && selectedCellCount > 0) {
          e.preventDefault();
          deleteSelectedRange();
        }
      } else if (e.key === 'Enter' || e.key === 'F2') {
        e.preventDefault();
        startEditing(currentStart.row, currentStart.col);
      } else if (e.key === ' ') {
        e.preventDefault();
        const colDef = COLUMNS_META[currentStart.col];
        if (colDef && colDef.type === 'checkbox') {
          const rowObj = monthlyRows[currentStart.row];
          if (rowObj) {
            handleCellChange(rowObj.dateStr, colDef.key, !(rowObj.data as any)[colDef.key]);
          }
        } else {
          startEditing(currentStart.row, currentStart.col);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (e.shiftKey) {
          const newEndR = Math.max(0, currentEnd.row - 1);
          setSelectionEnd({ row: newEndR, col: currentEnd.col });
          scrollToCell(newEndR, currentEnd.col);
        } else {
          const newR = Math.max(0, currentStart.row - 1);
          setSelectionStart({ row: newR, col: currentStart.col });
          setSelectionEnd({ row: newR, col: currentStart.col });
          scrollToCell(newR, currentStart.col);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (e.shiftKey) {
          const newEndR = Math.min(daysInMonth - 1, currentEnd.row + 1);
          setSelectionEnd({ row: newEndR, col: currentEnd.col });
          scrollToCell(newEndR, currentEnd.col);
        } else {
          const newR = Math.min(daysInMonth - 1, currentStart.row + 1);
          setSelectionStart({ row: newR, col: currentStart.col });
          setSelectionEnd({ row: newR, col: currentStart.col });
          scrollToCell(newR, currentStart.col);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (e.shiftKey) {
          const newEndC = Math.max(0, currentEnd.col - 1);
          setSelectionEnd({ row: currentEnd.row, col: newEndC });
          scrollToCell(currentEnd.row, newEndC);
        } else {
          const newC = Math.max(0, currentStart.col - 1);
          setSelectionStart({ row: currentStart.row, col: newC });
          setSelectionEnd({ row: currentStart.row, col: newC });
          scrollToCell(currentStart.row, newC);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (e.shiftKey) {
          const newEndC = Math.min(TOTAL_COLUMNS - 1, currentEnd.col + 1);
          setSelectionEnd({ row: currentEnd.row, col: newEndC });
          scrollToCell(currentEnd.row, newEndC);
        } else {
          const newC = Math.min(TOTAL_COLUMNS - 1, currentStart.col + 1);
          setSelectionStart({ row: currentStart.row, col: newC });
          setSelectionEnd({ row: currentStart.row, col: newC });
          scrollToCell(currentStart.row, newC);
        }
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const nextC = e.shiftKey ? Math.max(0, currentStart.col - 1) : Math.min(TOTAL_COLUMNS - 1, currentStart.col + 1);
        setSelectionStart({ row: currentStart.row, col: nextC });
        setSelectionEnd({ row: currentStart.row, col: nextC });
        scrollToCell(currentStart.row, nextC);
      } else if (
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.key.length === 1 &&
        /[\d.-]/.test(e.key)
      ) {
        const colDef = COLUMNS_META[currentStart.col];
        if (colDef && colDef.type !== 'checkbox') {
          e.preventDefault();
          startEditing(currentStart.row, currentStart.col, e.key);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    editingCell,
    isModalOpen,
    selectedRange,
    selectionStart,
    selectionEnd,
    selectedCellCount,
    daysInMonth,
    monthlyRows,
    personalReports,
    commitEdit,
    deleteSelectedRange,
    copySelectedRange,
    pasteFromClipboard,
    startEditing,
    scrollToCell,
    bulkUpdateDailyReports,
  ]);

  const handleExportCSV = () => {
    const entries = monthlyRows.map((r) => r.data);
    const csv = StorageService.exportPersonalReportCSV(entries, monthLabel);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Personal_Report_${monthPrefix}.csv`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  const isCompact = false;

  return (
    <div
      ref={tableContainerRef}
      tabIndex={0}
      className="space-y-3 select-none animate-fade-in pb-16 print:p-0 print:m-0 print:space-y-0 relative outline-hidden"
    >
      {/* Dynamic Landscape Print Styling */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape !important;
            margin: 4mm 5mm !important;
          }
          html, body {
            width: 100% !important;
            height: 100% !important;
            background: #ffffff !important;
            color: #0f172a !important;
            font-size: 7.5px !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .personal-report-print-sheet {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .personal-report-print-sheet table {
            width: 100% !important;
            font-size: 7px !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }
          .personal-report-print-sheet th,
          .personal-report-print-sheet td {
            height: 14px !important;
            max-height: 14px !important;
            line-height: 14px !important;
            padding: 0 !important;
            border: 0.5px solid #94a3b8 !important;
          }
          .personal-report-print-header {
            display: flex !important;
          }
        }
      `}</style>

      {/* Print-Only Sheet Header */}
      <div className="hidden personal-report-print-header items-center justify-between pb-1 mb-1 border-b-2 border-slate-700 text-slate-900 font-sans">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-black tracking-tight text-slate-900">MyLife</span>
          <span className="text-[9px] text-slate-600 font-bold">| Monthly Personal Accountability Sheet (মাসিক ব্যক্তিগত রিপোর্ট)</span>
        </div>
        <div className="text-center font-black text-xs text-slate-900 uppercase tracking-wide">
          {monthLabel}
        </div>
        <div className="text-right text-[9px] text-slate-700 font-bold">
          Name: <span className="underline">{user?.name || 'Tahsin Miftah'}</span>
        </div>
      </div>

      {/* Top Action Ribbon */}
      <div className="flex flex-col gap-2.5 p-2.5 sm:p-3 bg-white dark:bg-[#111827] border border-slate-200/90 dark:border-slate-800 rounded-2xl shadow-xs print:hidden">
        <div className="flex items-center justify-between gap-2 w-full">
          {/* Month Selector */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="flex items-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-xl p-0.5 sm:p-1 shadow-xs">
              <button
                onClick={prevMonth}
                className="p-1 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
                title="Previous Month"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="px-1.5 sm:px-2 text-xs font-black text-slate-800 dark:text-slate-200 whitespace-nowrap">
                {monthLabel}
              </span>
              <button
                onClick={nextMonth}
                className="p-1 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
                title="Next Month"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <span className="hidden sm:inline-block text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 flex-shrink-0">
              {daysInMonth} Days
            </span>
          </div>

          {/* View Density & Action Buttons - Single Scrollable Row on Mobile */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap justify-end flex-1 min-w-0">

            {/* Export CSV */}
            <button
              onClick={handleExportCSV}
              className="px-2.5 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 hover:bg-slate-100 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold shadow-xs flex items-center gap-1 transition-colors flex-shrink-0"
              title="Download CSV"
            >
              <Download className="w-3 h-3" />
              <span>CSV</span>
            </button>

            {/* Print 1-Page (Landscape) */}
            <button
              onClick={handlePrint}
              className="px-2.5 sm:px-3 py-1 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold shadow-xs flex items-center gap-1 transition-all flex-shrink-0"
              title="Print Monthly Report"
            >
              <Printer className="w-3 h-3" />
              <span className="hidden sm:inline">Print 1-Page</span>
              <span className="sm:hidden">Print</span>
            </button>
          </div>
        </div>

        {/* Google Sheets Active Cell Indicator Pill */}
        {activeCellInfo && (
          <div className="flex items-center justify-between sm:justify-start gap-2 px-3 py-1.5 bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/30 rounded-xl text-xs font-medium text-slate-800 dark:text-slate-200 shadow-2xs w-full">
            <div className="flex items-center gap-1.5 truncate">
              <span className="font-mono text-slate-500 dark:text-slate-400 font-bold">{activeCellInfo.date}</span>
              <span className="text-slate-300 dark:text-slate-600">›</span>
              <span className="font-bold text-slate-900 dark:text-white truncate">{activeCellInfo.field}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-slate-400 text-[10px]">মান:</span>
              <span className="font-mono font-black text-emerald-600 dark:text-emerald-400 bg-white dark:bg-slate-900 px-2 py-0.5 rounded border border-emerald-500/20">
                {activeCellInfo.value}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Copy Toast Feedback */}
      {copyFeedback && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl shadow-lg animate-bounce text-xs font-bold">
          <Check className="w-4 h-4" />
          <span>Copied cells to clipboard (Google Sheets format)!</span>
        </div>
      )}

      {/* Main Spreadsheet Matrix Table */}
      <div className="personal-report-print-sheet bg-white dark:bg-[#111827] border border-slate-300 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden select-none print:border-none print:shadow-none print:rounded-none">
        <div className="overflow-x-auto overflow-y-auto max-h-[85vh] print:max-h-none print:overflow-visible">
          <table
            className={`w-full border-collapse text-center select-none table-fixed ${
              isCompact ? 'text-[10px] leading-none min-w-[980px]' : 'text-xs min-w-[1100px]'
            }`}
          >
            {/* Column Width Specifications to guarantee fixed compact cell sizes that never stretch */}
            <colgroup>
              <col style={{ width: isCompact ? '58px' : '72px' }} />
              {COLUMNS_META.map((col) => (
                <col
                  key={col.key}
                  style={{
                    width:
                      col.type === 'checkbox'
                        ? isCompact ? '22px' : '26px'
                        : isCompact ? '32px' : '38px',
                  }}
                />
              ))}
              <col style={{ width: '22px' }} />
            </colgroup>

            {/* Header Tier 1: Group Categories */}
            <thead className="sticky top-0 z-20 font-bold border-b border-slate-300 dark:border-slate-700">
              <tr className={isCompact ? 'h-5' : 'h-7'}>
                {/* Top Left All-Selector */}
                <th
                  rowSpan={2}
                  onClick={handleSelectAll}
                  className="px-1 border-r border-b border-slate-300 dark:border-slate-700 sticky left-0 z-30 bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-white text-left font-black cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-800 transition-colors"
                  title="Click to select all cells (Ctrl+A)"
                >
                  <div className="flex items-center justify-between">
                    <span>Date</span>
                    <span className="text-[8px] text-slate-400 font-mono">⛶</span>
                  </div>
                </th>

                {/* Quran Study */}
                <th colSpan={2} className="px-0.5 border-r border-b border-emerald-300 dark:border-emerald-800 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-200">
                  Quran
                </th>

                {/* Hadith Study */}
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 2)}
                  className="px-0.5 border-r border-b border-purple-300 dark:border-purple-800 bg-purple-100 dark:bg-purple-950/70 text-purple-900 dark:text-purple-200 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/80 transition-colors"
                  title="Click to select Hadith column"
                >
                  Hadith
                </th>

                {/* Salah */}
                <th colSpan={2} className="px-0.5 border-r border-b border-sky-300 dark:border-sky-800 bg-sky-100 dark:bg-sky-950/70 text-sky-900 dark:text-sky-200">
                  Salah
                </th>

                {/* Literature */}
                <th colSpan={2} className="px-0.5 border-r border-b border-amber-300 dark:border-amber-800 bg-amber-100 dark:bg-amber-950/70 text-amber-900 dark:text-amber-200">
                  Literature
                </th>

                {/* Academic */}
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 7)}
                  className="px-0.5 border-r border-b border-yellow-300 dark:border-yellow-800 bg-yellow-100 dark:bg-yellow-950/70 text-yellow-900 dark:text-yellow-200 cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-900/80 transition-colors"
                  title="Click to select Academic column"
                >
                  Acad
                </th>

                {/* Classes */}
                <th colSpan={2} className="px-0.5 border-r border-b border-teal-300 dark:border-teal-800 bg-teal-100 dark:bg-teal-950/70 text-teal-900 dark:text-teal-200">
                  Class
                </th>

                {/* Contacts */}
                <th colSpan={5} className="px-0.5 border-r border-b border-indigo-300 dark:border-indigo-800 bg-indigo-100 dark:bg-indigo-950/70 text-indigo-900 dark:text-indigo-200">
                  Contacts
                </th>

                {/* Special Outreach */}
                <th colSpan={3} className="px-0.5 border-r border-b border-cyan-300 dark:border-cyan-800 bg-cyan-100 dark:bg-cyan-950/70 text-cyan-900 dark:text-cyan-200">
                  Outreach
                </th>

                {/* Distribution */}
                <th colSpan={4} className="px-0.5 border-r border-b border-pink-300 dark:border-pink-800 bg-pink-100 dark:bg-pink-950/70 text-pink-900 dark:text-pink-200">
                  Dist
                </th>

                {/* Org Duties */}
                <th colSpan={2} className="px-0.5 border-r border-b border-rose-300 dark:border-rose-800 bg-rose-100 dark:bg-rose-950/70 text-rose-900 dark:text-rose-200">
                  Duties
                </th>

                {/* Social Media */}
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 24)}
                  className="px-0.5 border-r border-b border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                  title="Click to select Social column"
                >
                  Social
                </th>

                {/* Checkboxes */}
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 25)}
                  className="px-0 border-r border-b border-emerald-300 dark:border-emerald-800 bg-emerald-100/80 dark:bg-emerald-950/60 text-emerald-900 dark:text-emerald-200 cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-900/80 transition-colors"
                  title="Click to select Newspaper column"
                >
                  Pap
                </th>
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 26)}
                  className="px-0 border-r border-b border-rose-300 dark:border-rose-800 bg-rose-100/80 dark:bg-rose-950/60 text-rose-900 dark:text-rose-200 cursor-pointer hover:bg-rose-200 dark:hover:bg-rose-900/80 transition-colors"
                  title="Click to select Exercise column"
                >
                  Ex
                </th>
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 27)}
                  className="px-0 border-r border-b border-indigo-300 dark:border-indigo-800 bg-indigo-100/80 dark:bg-indigo-950/60 text-indigo-900 dark:text-indigo-200 cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-900/80 transition-colors"
                  title="Click to select Muhasabah column"
                >
                  Muh
                </th>

                {/* Sleep */}
                <th
                  rowSpan={2}
                  onClick={(e) => handleColHeaderClick(e, 28)}
                  className="px-0.5 border-r border-b border-blue-300 dark:border-blue-800 bg-blue-100 dark:bg-blue-950/70 text-blue-900 dark:text-blue-200 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/80 transition-colors"
                  title="Click to select Sleep column"
                >
                  Sleep
                </th>

                {/* Edit */}
                <th rowSpan={2} className="px-0 border-b border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 text-slate-600 print:hidden">
                  ✎
                </th>
              </tr>

              {/* Header Tier 2: Sub-Columns */}
              <tr className={`bg-slate-50 dark:bg-slate-900/90 text-slate-600 dark:text-slate-300 ${isCompact ? 'h-4 text-[9px]' : 'h-6 text-[11px]'}`}>
                {/* Quran */}
                <th onClick={(e) => handleColHeaderClick(e, 0)} className="px-0 border-r border-b border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Til</th>
                <th onClick={(e) => handleColHeaderClick(e, 1)} className="px-0 border-r border-b border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Taf</th>

                {/* Salah */}
                <th onClick={(e) => handleColHeaderClick(e, 3)} className="px-0 border-r border-b border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Jam</th>
                <th onClick={(e) => handleColHeaderClick(e, 4)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Qaz</th>

                {/* Literature */}
                <th onClick={(e) => handleColHeaderClick(e, 5)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Gen</th>
                <th onClick={(e) => handleColHeaderClick(e, 6)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Syl</th>

                {/* Classes */}
                <th onClick={(e) => handleColHeaderClick(e, 8)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Hld</th>
                <th onClick={(e) => handleColHeaderClick(e, 9)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Att</th>

                {/* Contacts */}
                <th onClick={(e) => handleColHeaderClick(e, 10)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Mbr</th>
                <th onClick={(e) => handleColHeaderClick(e, 11)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Asc</th>
                <th onClick={(e) => handleColHeaderClick(e, 12)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Wrk</th>
                <th onClick={(e) => handleColHeaderClick(e, 13)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Sup</th>
                <th onClick={(e) => handleColHeaderClick(e, 14)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Frn</th>

                {/* Special Outreach */}
                <th onClick={(e) => handleColHeaderClick(e, 15)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Tln</th>
                <th onClick={(e) => handleColHeaderClick(e, 16)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Wsh</th>
                <th onClick={(e) => handleColHeaderClick(e, 17)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Cnt</th>

                {/* Distribution */}
                <th onClick={(e) => handleColHeaderClick(e, 18)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Bk</th>
                <th onClick={(e) => handleColHeaderClick(e, 19)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Mg</th>
                <th onClick={(e) => handleColHeaderClick(e, 20)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Stk</th>
                <th onClick={(e) => handleColHeaderClick(e, 21)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Gft</th>

                {/* Duties */}
                <th onClick={(e) => handleColHeaderClick(e, 22)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Daw</th>
                <th onClick={(e) => handleColHeaderClick(e, 23)} className="px-0 border-r border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800">Oth</th>
              </tr>
            </thead>

            {/* Table Rows (01 to 31) */}
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800 font-semibold text-slate-800 dark:text-slate-200">
              {monthlyRows.map((row, rIdx) => {
                const isFri = row.isFriday;
                const isTod = row.isToday;
                const d = row.data as any;

                const rowBg = isTod
                  ? 'bg-emerald-50 dark:bg-emerald-950/30'
                  : isFri
                  ? 'bg-[#fff1f2] dark:bg-rose-950/20 hover:bg-[#ffe4e6] dark:hover:bg-rose-950/30'
                  : 'bg-white dark:bg-[#111827] hover:bg-slate-50 dark:hover:bg-slate-800/40';

                return (
                  <tr
                    key={row.dateStr}
                    className={`${rowBg} transition-colors ${isCompact ? 'h-[18px] max-h-[18px]' : 'h-6'}`}
                  >
                    {/* Sticky Date Row Header */}
                    <td
                      onClick={(e) => handleRowHeaderClick(e, rIdx)}
                      className={`px-0.5 text-left border-r border-slate-300 dark:border-slate-800 sticky left-0 z-10 font-bold whitespace-nowrap cursor-pointer overflow-hidden text-ellipsis hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors ${
                        selectionStart?.row === rIdx
                          ? 'bg-emerald-200 dark:bg-emerald-900 text-emerald-950 dark:text-emerald-100 font-black border-r-2 border-r-emerald-600'
                          : isTod
                          ? 'bg-emerald-100 dark:bg-emerald-900/70 text-emerald-950 dark:text-emerald-200 font-black'
                          : isFri
                          ? 'bg-rose-100 dark:bg-rose-950/70 text-rose-900 dark:text-rose-300'
                          : 'bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-200'
                      }`}
                      title="Click to select this entire row"
                    >
                      <div className="flex items-center justify-between gap-0.5 overflow-hidden">
                        <span className="truncate">
                          {String(row.dayNum).padStart(2, '0')}, {row.dayName}
                        </span>
                        {isTod && <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-emerald-500" />}
                      </div>
                    </td>

                    {/* Dynamic 29 Column Cells (Interactive Spreadsheet Grid with Google Sheets 1-Click Select & Double-Click Edit) */}
                    {COLUMNS_META.map((col, cIdx) => {
                      const isSel = isCellSelected(rIdx, cIdx);
                      const isAnchor = isAnchorCell(rIdx, cIdx);
                      const inFill = isCellInFill(rIdx, cIdx);
                      const isEditing = editingCell?.row === rIdx && editingCell?.col === cIdx;
                      const rawVal = d[col.key];

                      // Bounding Box Edge Calculations for Google Sheets Style Outline
                      const isSingleCellSelected = isAnchor && isSel && selectedCellCount <= 1;
                      const isTopEdge = isSingleCellSelected || (selectedRange && rIdx === selectedRange.minRow && cIdx >= selectedRange.minCol && cIdx <= selectedRange.maxCol);
                      const isBottomEdge = isSingleCellSelected || (selectedRange && rIdx === selectedRange.maxRow && cIdx >= selectedRange.minCol && cIdx <= selectedRange.maxCol);
                      const isLeftEdge = isSingleCellSelected || (selectedRange && cIdx === selectedRange.minCol && rIdx >= selectedRange.minRow && rIdx <= selectedRange.maxRow);
                      const isRightEdge = isSingleCellSelected || (selectedRange && cIdx === selectedRange.maxCol && rIdx >= selectedRange.minRow && rIdx <= selectedRange.maxRow);
                      const isCorner = selectedRange && rIdx === selectedRange.maxRow && cIdx === selectedRange.maxCol;

                      // Cell Display Value
                      let displayVal = '';
                      if (col.type !== 'checkbox') {
                        if (rawVal !== undefined && rawVal !== null && rawVal !== '') {
                          displayVal = String(rawVal);
                        }
                      }

                      return (
                        <td
                          id={`cell-${rIdx}-${cIdx}`}
                          key={col.key}
                          data-row={rIdx}
                          data-col={cIdx}
                          onMouseDown={(e) => handleCellMouseDown(e, rIdx, cIdx)}
                          onMouseEnter={() => handleCellMouseEnter(rIdx, cIdx)}
                          onClick={() => handleCellClick(rIdx, cIdx)}
                          onDoubleClick={() => handleCellDoubleClick(rIdx, cIdx)}
                          className={`p-0 border-r border-slate-200 dark:border-slate-800 select-none relative cursor-cell transition-none overflow-hidden max-w-full ${
                            isCompact ? 'h-[18px]' : 'h-6'
                          } ${
                            isSel
                              ? 'bg-emerald-500/15 dark:bg-emerald-500/25'
                              : inFill
                              ? 'bg-sky-500/20 dark:bg-sky-500/30'
                              : ''
                          }`}
                          style={{
                            borderTop: isTopEdge ? '2px solid #059669' : undefined,
                            borderBottom: isBottomEdge ? '2px solid #059669' : undefined,
                            borderLeft: isLeftEdge ? '2px solid #059669' : undefined,
                            borderRight: isRightEdge ? '2px solid #059669' : undefined,
                          }}
                        >
                          {/* Active Inline Input Editor (Fixed dimensions that never stretch the column) */}
                          {isEditing ? (
                            <input
                              ref={editInputRef}
                              type="text"
                              inputMode={col.type === 'decimal' ? 'decimal' : 'numeric'}
                              pattern={col.type === 'decimal' ? '[0-9]*[.,]?[0-9]*' : '[0-9]*'}
                              size={1}
                              value={editValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (col.type === 'decimal') {
                                  if (/^[0-9]*\.?[0-9]*$/.test(val)) {
                                    setEditValue(val);
                                  }
                                } else {
                                  if (/^[0-9]*$/.test(val)) {
                                    setEditValue(val);
                                  }
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitEdit({ rowOffset: e.shiftKey ? -1 : 1, colOffset: 0 });
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  commitEdit({ rowOffset: 0, colOffset: e.shiftKey ? -1 : 1 });
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelEdit();
                                }
                              }}
                              onBlur={() => commitEdit()}
                              autoComplete="off"
                              autoCorrect="off"
                              spellCheck={false}
                              style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
                              className={`w-full h-full min-w-0 max-w-full text-center bg-white dark:bg-slate-900 border-2 border-emerald-500 outline-hidden font-bold text-slate-900 dark:text-white z-30 relative shadow-sm ${
                                isCompact ? 'text-[10px] p-0' : 'text-xs px-0.5'
                              }`}
                            />
                          ) : col.type === 'checkbox' ? (
                            /* Checkbox Cell */
                            <div className="w-full h-full flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!rawVal}
                                onChange={() => handleCellChange(row.dateStr, col.key, !rawVal)}
                                className="w-3.5 h-3.5 cursor-pointer accent-emerald-600 rounded"
                              />
                            </div>
                          ) : (
                            /* Number / Text Cell (1-Click Input Ready) */
                            <div
                              className={`w-full h-full min-w-0 flex items-center justify-center font-bold overflow-hidden whitespace-nowrap text-ellipsis px-0.5 cursor-text ${
                                isCompact ? 'text-[10px]' : 'text-xs'
                              } ${col.textColor || ''} ${isAnchor && isSel ? 'ring-1 ring-inset ring-emerald-600 dark:ring-emerald-400' : ''}`}
                            >
                              {displayVal}
                            </div>
                          )}

                          {/* Google Sheets Fill Handle (Small Square on Bottom-Right Corner of Selection) */}
                          {isCorner && !isEditing && (
                            <div
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setIsFillDragging(true);
                                setFillEnd(selectionEnd);
                              }}
                              className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-emerald-600 dark:bg-emerald-400 border border-white dark:border-slate-900 rounded-2xs cursor-crosshair z-20 shadow-xs hover:scale-125 transition-transform"
                              title="Drag down or right to auto-fill values"
                            />
                          )}
                        </td>
                      );
                    })}

                    {/* Action: Open Day Modal */}
                    <td className="p-0 text-center print:hidden">
                      <button
                        onClick={() => {
                          setSelectedDateForModal(row.dateStr);
                          setIsModalOpen(true);
                        }}
                        className="p-0.5 text-slate-400 hover:text-emerald-600 transition-colors"
                        title="Edit Full Day Details"
                      >
                        <Edit2 className="w-2.5 h-2.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Bottom Total Summary Row */}
            <tfoot className="bg-slate-100 dark:bg-slate-900 sticky bottom-0 z-20 font-black border-t-2 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white">
              <tr className={isCompact ? 'h-5 text-[10px]' : 'h-7 text-xs'}>
                <td className="px-0.5 text-left border-r border-slate-300 dark:border-slate-700 sticky left-0 z-30 bg-slate-200 dark:bg-slate-800 font-black truncate">
                  Total:
                </td>

                {COLUMNS_META.map((col) => {
                  if (col.key === 'sleepHours') {
                    return (
                      <td key={col.key} className="px-0 border-r border-slate-200 dark:border-slate-800 text-blue-700 dark:text-blue-300 font-black truncate">
                        {totals.avgSleep}
                      </td>
                    );
                  }

                  const sum = totals.sumMap[col.key] || 0;
                  return (
                    <td
                      key={col.key}
                      className={`px-0 border-r border-slate-200 dark:border-slate-800 font-black truncate ${
                        col.textColor || 'text-slate-800 dark:text-slate-200'
                      }`}
                    >
                      {col.type === 'decimal' ? sum.toFixed(1) : sum}
                      {col.type === 'checkbox' && 'd'}
                    </td>
                  );
                })}

                <td className="p-0 text-emerald-600 font-black print:hidden">✓</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Google Sheets Status Bar & Live Selection Statistics Dock */}
      {selectedRange && selectedCellCount > 1 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center flex-wrap gap-3 px-4 py-2 bg-slate-900/95 dark:bg-slate-950/95 text-white backdrop-blur-md border border-slate-700/80 rounded-2xl shadow-2xl text-xs font-semibold print:hidden animate-fade-in">
          {/* Selected Cell Count */}
          <div className="flex items-center gap-1.5 text-emerald-400 font-bold pr-2 border-r border-slate-700">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
            <span>{selectedCellCount} cells</span>
          </div>

          {/* Numerical Aggregates */}
          {selectionStats && selectionStats.numCount > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Sum:</span>
                <span className="text-emerald-300 font-bold">{selectionStats.numSum}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Avg:</span>
                <span className="text-sky-300 font-bold">{selectionStats.avg}</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-400 hidden sm:flex">
                <span>Min:</span>
                <span className="text-slate-200 font-semibold">{selectionStats.min}</span>
                <span className="ml-1">Max:</span>
                <span className="text-slate-200 font-semibold">{selectionStats.max}</span>
              </div>
            </div>
          )}

          {/* Checkboxes Count */}
          {selectionStats && selectionStats.checkCount > 0 && (
            <div className="flex items-center gap-1 text-emerald-400">
              <span className="text-slate-400">Checked:</span>
              <span className="font-bold">{selectionStats.checkCount}</span>
            </div>
          )}

          {/* Quick Action Buttons */}
          <div className="flex items-center gap-1.5 pl-2 border-l border-slate-700">
            <button
              onClick={copySelectedRange}
              className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors"
              title="Copy to clipboard (Ctrl+C)"
            >
              <Copy className="w-3 h-3" />
              <span>Copy</span>
            </button>
            <button
              onClick={deleteSelectedRange}
              className="px-2 py-1 bg-rose-600/90 hover:bg-rose-600 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors"
              title="Clear selected cells (Delete)"
            >
              <Trash2 className="w-3 h-3" />
              <span>Delete</span>
            </button>
            <button
              onClick={() => {
                setSelectionStart(null);
                setSelectionEnd(null);
              }}
              className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors"
              title="Clear selection (Esc)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Daily Modal */}
      <DailyReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialDate={selectedDateForModal}
      />
    </div>
  );
};
